import { useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TextArea } from '~/components/ui/inputs/TextArea';
import { useGetBuyListLocations } from '~/api/location';
import { getLocalDateString } from '~/utils';
import { LocationAndVehicleSelects } from '~/pages/buy-lists/sections/LocationAndVehicleSelects';

export type AddToBuyListDraft = {
  locationId: string;
  transportVehicleId: string;
  expectedShipDate: string;
  note: string;
};

type AddToBuyListCreateFormProps = {
  initialLocationId: string;
  onBack?: () => void;
  onDraftChange: (draft: AddToBuyListDraft | null) => void;
};

const BUY_LIST_NOTE_MAX_LENGTH = 2000;

export function AddToBuyListCreateForm({
  initialLocationId,
  onBack,
  onDraftChange,
}: Readonly<AddToBuyListCreateFormProps>) {
  const { t } = useTranslation('inventory');
  const [chosenLocationId, setChosenLocationId] = useState<string | null>(null);
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(
    null
  );
  const [expectedShipDate, setExpectedShipDate] = useState('');
  const [note, setNote] = useState('');

  const { data: locationsData } = useGetBuyListLocations();
  const locations = locationsData?.buyListLocations ?? [];

  const isInitialLocationOffered = locations.some(
    (location) => location.id === initialLocationId
  );
  const selectedLocationId =
    chosenLocationId ?? (isInitialLocationOffered ? initialLocationId : null);

  useEffect(() => {
    onDraftChange(
      selectedLocationId !== null &&
        selectedVehicleId !== null &&
        expectedShipDate !== ''
        ? {
            locationId: selectedLocationId,
            transportVehicleId: selectedVehicleId,
            expectedShipDate,
            note,
          }
        : null
    );
  }, [
    selectedLocationId,
    selectedVehicleId,
    expectedShipDate,
    note,
    onDraftChange,
  ]);

  return (
    <div
      className="border border-neut-200 rounded-lg p-4 space-y-3"
      data-testid="add-to-buy-list-create-form"
    >
      {onBack && (
        <button
          type="button"
          className="flex items-center gap-1 text-sm text-prim hover:text-prim-hover cursor-pointer"
          onClick={onBack}
          data-testid="add-to-buy-list-create-back"
        >
          <ArrowLeft size={16} aria-hidden="true" />
          {t('inventory.buy_list.back_to_lists')}
        </button>
      )}

      <LocationAndVehicleSelects
        locations={locations}
        locationId={selectedLocationId}
        transportVehicleId={selectedVehicleId}
        onLocationChange={setChosenLocationId}
        onTransportVehicleChange={setSelectedVehicleId}
        locationTestId="add-to-buy-list-location-select"
        vehicleTestId="add-to-buy-list-vehicle-select"
      />

      <label
        className="block text-sm font-medium text-neut-700"
        htmlFor="add-to-buy-list-expected-order-date"
      >
        {t('inventory.buy_list.ship_week')}
      </label>
      <input
        id="add-to-buy-list-expected-order-date"
        data-testid="add-to-buy-list-date-input"
        type="date"
        min={getLocalDateString()}
        className="w-full px-3 py-2 text-sm border border-neut-300 rounded-md focus:outline-none focus:ring-1 focus:ring-prim"
        value={expectedShipDate}
        onChange={(event) => setExpectedShipDate(event.target.value)}
      />

      <div data-testid="add-to-buy-list-note-field">
        <TextArea
          id="add-to-buy-list-note"
          name="buy-list-note"
          label={t('inventory.buy_list.note_label')}
          placeholder={t('inventory.buy_list.note_placeholder')}
          rows={3}
          maxLength={BUY_LIST_NOTE_MAX_LENGTH}
          value={note}
          onChange={(_name, value) => setNote(value)}
        />
      </div>
    </div>
  );
}
